import { useState } from 'react';
import {
  discountPct,
  formatCents,
  relativeAge,
  urlDomain,
  type Deal,
  type DealStatus,
} from '../shared/deals';
import { ScoreBadge } from './ScoreBadge';

// Reddit thumbnails are hotlinked and go dead; the fallback is a typographic
// tile rather than a broken-image icon, so a dead link costs no layout shift.
function Thumb({ deal }: { deal: Deal }) {
  const [failed, setFailed] = useState(false);
  const initial = (deal.category ?? '?')[0].toUpperCase();

  if (!deal.thumbnailUrl || failed) {
    return (
      <div
        aria-hidden="true"
        className="flex h-18 w-18 shrink-0 items-center justify-center border border-rule bg-paper-deep"
      >
        <span className="display-sm text-2xl text-ink-faint">{initial}</span>
      </div>
    );
  }
  return (
    <img
      src={deal.thumbnailUrl}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-18 w-18 shrink-0 border border-rule object-cover"
    />
  );
}

export function FeedCard({
  deal,
  onStatus,
  actions,
  busy,
}: {
  deal: Deal;
  onStatus: (status: DealStatus) => void;
  actions: ReadonlyArray<{ label: string; status: DealStatus }>;
  busy: boolean;
}) {
  const price = formatCents(deal.priceCents);
  const list = formatCents(deal.listPriceCents);
  const off = discountPct(deal.priceCents, deal.listPriceCents);
  const domain = urlDomain(deal.url);
  const age = relativeAge(deal.postedAt);

  return (
    <article className="border-b border-rule py-5">
      <div className="flex gap-3">
        <Thumb deal={deal} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="min-w-0 text-[0.9375rem] leading-snug font-medium">
              <a
                href={deal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-2 hover:underline"
              >
                {deal.title}
              </a>
            </h3>
            <ScoreBadge score={deal.score} />
          </div>

          <p className="mt-1.5 font-mono text-[0.6875rem] tracking-wide text-ink-faint">
            {[domain, deal.category, age].filter(Boolean).join(' · ')}
          </p>

          {price && (
            <p className="mt-2 flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-sm font-semibold tabular">{price}</span>
              {list && off != null && (
                <span className="font-mono text-xs text-ink-faint line-through tabular">{list}</span>
              )}
              {off != null && (
                <span className="font-mono text-xs font-medium text-positive tabular">
                  {off}% off
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* The why-line is the scorer talking, so it is set as a critic's note
          rather than as body copy. */}
      {deal.reason && (
        <p className="mt-3 max-w-[62ch] border-l-2 border-rule pl-3 font-display text-sm leading-snug text-ink-soft italic">
          {deal.reason}
        </p>
      )}

      <div className="mt-3 flex items-center gap-4">
        {actions.map((action) => (
          <button
            key={action.status}
            type="button"
            disabled={busy}
            onClick={() => onStatus(action.status)}
            className="font-mono text-[0.6875rem] tracking-[0.12em] text-ink-faint uppercase underline underline-offset-4 transition-colors hover:text-ink disabled:opacity-40"
          >
            {action.label}
          </button>
        ))}
        <a
          href={deal.permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto font-mono text-[0.6875rem] tracking-[0.12em] text-ink-faint uppercase underline underline-offset-4 hover:text-ink"
        >
          Thread
        </a>
      </div>
    </article>
  );
}
